import { Controller } from '@hotwired/stimulus'
import { t } from '@lingui/core/macro'

export default class extends Controller {
  connect() {
    // WINDOW PROPERTIES
    this.windowInnerWidth = window.innerWidth
    this.documentInnerWidth = document.documentElement.clientWidth
    this.scrollbarWidth = this.windowInnerWidth - this.documentInnerWidth
    this.pageContainsScroll = this.windowInnerWidth > this.documentInnerWidth

    // CHAT WIDGET PROPERTIES
    this.chat = document.querySelector('.chat-app')
    this.isResizing = false
    this.isClosed = true
    this.isMaximized = false
    this.originalWidth = '768px'

    // CHAT LAUNCHER PROPERTIES
    this.launcherButton = this.element.querySelector('.chat-btn')
    this.launcherClose = this.launcherButton.querySelector('.chat-btn-close')
    this.isDragging = false
    this.startX = 0
    this.startY = 0
    this.dragThreshold = 5

    // Add specific event listener to close button
    if (this.launcherClose) {
      this.launcherClose.addEventListener('click', this.onLauncherClose.bind(this))
    }

    // Add event listeners to scroll to bottom when new message is added
    document.addEventListener('turbo:before-stream-render', this.onTurboRender.bind(this))
    document.addEventListener('turbo:frame-render', this.onTurboRender.bind(this))
  }

  disconnect() {
    // clean up
    document.removeEventListener('turbo:before-stream-render', this.onTurboRender.bind(this))
    document.removeEventListener('turbo:frame-render', this.onTurboRender.bind(this))
  }

  onLauncherClose(event) {
    event.stopPropagation()
    event.preventDefault()
    this.launcherButton.style.display = 'none'
  }

  onTurboRender(event) {
    // Check if event is related to chat by inspecting various properties
    const isChatRelated = this.isChatRelatedEvent(event)

    if (!isChatRelated) {
      return
    }

    // Scroll with appropriate delay based on event type
    setTimeout(() => this.scrollToBottom(), 10)
  }

  isChatRelatedEvent(event) {
    // Check turbo stream targets
    if (event.type === 'turbo:before-stream-render') {
      const streamElement = event.detail?.newStream
      if (streamElement) {
        const target = streamElement.getAttribute('target')
        // const action = streamElement.getAttribute('action')

        // Check if target is chat-related
        if (target && (target.startsWith('chat_') || target.includes('message') || target === 'chat-messages')) {
          return true
        }

        // Check if the stream content contains chat-related classes
        const template = streamElement.querySelector('template')
        if (template?.content) {
          const hasMessage = template.content.querySelector('.message')
          const hasTyping = template.content.querySelector('.typing, .typing-indicator')
          if (hasMessage || hasTyping) {
            return true
          }
        }
      }
    }

    // Check turbo frame renders
    if (event.type === 'turbo:frame-render') {
      const target = event.target

      // Check if the frame itself is inside chat-messages
      if (target.closest('.chat-messages')) {
        return true
      }

      // Check if frame has chat-related ID
      const frameId = target.id || ''
      if (frameId.startsWith('chat') || frameId.includes('message')) {
        return true
      }

      // Check if frame content has message or typing elements
      const hasMessage = target.querySelector('.message')
      const hasTyping = target.querySelector('.typing, .typing-indicator')
      if (hasMessage || hasTyping) {
        return true
      }
    }

    return false
  }

  onMessageSubmit(event) {
    const textarea = event.target.querySelector('.dynamic-textarea')
    const value = textarea.value.trim()

    const abort = () => {
      event.preventDefault()
      textarea.focus()
    }

    // Abort if the message is empty
    if (value === '') {
      return abort()
    }

    // Abort if the last message is still in progress
    if (
      document.querySelector('.chat-messages>.message:last-child>.typing-indicator') ||
      document.querySelector('.chat-messages>.message:last-child>.typing')
    ) {
      return abort()
    }

    // Go ahead and clear the textarea after a short delay
    setTimeout(() => {
      this.clearTextarea(event)
    }, 10)
  }

  onMessageEnterPress(event) {
    const value = event.target.value.trim()

    // Abort if the message is empty
    if (value === '') {
      return
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()

      // Submit the form by firing a click event on the submit button
      document.querySelector('.chat-submit').click()
    }
  }

  startLauncherDrag(event) {
    // Abort if the close button was clicked
    if (event.target.closest('.chat-btn-close')) {
      return
    }

    event.preventDefault()

    this.startX = event.clientX
    this.startY = event.clientY
    this.isDragging = false

    this.boundDrag = this.dragLauncher.bind(this)
    this.boundStopDrag = this.stopLauncherDrag.bind(this)

    // Add event listeners for dragging and stopping drag
    document.addEventListener('mousemove', this.boundDrag)
    document.addEventListener('mouseup', this.boundStopDrag)
  }

  dragLauncher(event) {
    const deltaX = Math.abs(event.clientX - this.startX)
    const deltaY = Math.abs(event.clientY - this.startY)

    if (deltaX > this.dragThreshold || deltaY > this.dragThreshold) {
      this.isDragging = true

      const offsetX = event.clientX - this.startX
      const offsetY = event.clientY - this.startY

      const currentLeft = parseInt(this.launcherButton.style.left || this.launcherButton.offsetLeft, 10)
      const currentTop = parseInt(this.launcherButton.style.top || this.launcherButton.offsetTop, 10)

      this.launcherButton.style.position = 'fixed'
      this.launcherButton.style.left = `${currentLeft + offsetX}px`
      this.launcherButton.style.top = `${currentTop + offsetY}px`

      this.startX = event.clientX
      this.startY = event.clientY
    }
  }

  stopLauncherDrag(event) {
    // Remove event listeners for dragging and stopping drag
    document.removeEventListener('mousemove', this.boundDrag)
    document.removeEventListener('mouseup', this.boundStopDrag)

    // Prevent toggle if close button was clicked or dragging occurred
    if (!this.isDragging && !event.target.closest('.chat-btn-close')) {
      this.toggleChat(event)
    }

    this.isDragging = false
  }

  toggleChat(_event) {
    if (this.chat.classList.contains('d-none')) {
      this.chat.classList.remove('d-none')
      this.isClosed = false
      this.scrollToBottom()
    } else {
      this.chat.classList.add('d-none')
      this.isClosed = true
    }
  }

  toggleSidebar(_event) {
    document.querySelector('.chat-sidebar').classList.toggle('collapsed')
  }

  toggleMaximize(_event) {
    const maximizeButton = document.getElementById('chat-maximize-button')
    const maximizeIcon = maximizeButton.querySelector('svg use')

    if (this.isMaximized) {
      // Restore to original size
      this.chat.style.width = this.originalWidth
      this.chat.classList.remove('maximized')
      maximizeButton.title = t`Maximize`
      // Change icon to maximize
      if (maximizeIcon) {
        maximizeIcon.setAttribute('href', maximizeIcon.getAttribute('href').replace('minimize', 'maximize'))
      }
      this.isMaximized = false
    } else {
      // Save current width and maximize
      this.originalWidth = this.chat.style.width || '768px'
      this.chat.style.width = '100%'
      this.chat.classList.add('maximized')
      maximizeButton.title = t`Restore`
      // Change icon to minimize
      if (maximizeIcon) {
        maximizeIcon.setAttribute('href', maximizeIcon.getAttribute('href').replace('maximize', 'minimize'))
      }
      this.isMaximized = true
    }
  }

  scrollToBottom() {
    const messagesContainer = document.querySelector('.chat-messages')

    if (messagesContainer) {
      setTimeout(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight
      }, 50)
    }
  }

  resizeTextarea() {
    const maxHeight = 280
    const border = 2
    const textarea = document.querySelector('.dynamic-textarea')

    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight + border, maxHeight + border)}px`
  }

  clearTextarea(event) {
    event.target.reset()
    this.resizeTextarea()
  }

  resizeChat() {
    const resizeHandle = document.querySelector('.resize-handle')
    const chatApp = document.querySelector('.chat-app')

    // abort if the resize handle is already initialized
    if (this.isResizingInitialized) {
      return
    }

    this.isResizing = false

    const onMouseMove = (e) => {
      if (this.isResizing) {
        const deltaX = this.startX - e.clientX
        let newWidth = this.startWidth + deltaX

        newWidth = Math.min(Math.max(newWidth, this.minWidth), this.maxWidth)
        chatApp.style.width = `${newWidth}px`

        // Handle sidebar collapse
        const chatMain = document.querySelector('.chat-main')
        const sidebar = document.querySelector('.chat-sidebar')
        const direction = deltaX > 0 ? 'left' : 'right'
        const collapsed = sidebar.classList.contains('collapsed')

        // Collapse/Expand the sidebar when user is resizing the chat
        if (direction === 'right' && chatMain.offsetWidth < 518 && !collapsed) {
          sidebar.classList.add('collapsed')
        } else if (direction === 'left' && chatMain.offsetWidth > 518 && collapsed) {
          sidebar.classList.remove('collapsed')
        }
      }
    }

    const onMouseUp = () => {
      this.isResizing = false
      document.body.style.userSelect = 'auto'
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }

    const onMouseDown = (e) => {
      e.preventDefault()
      this.isResizing = true
      document.body.style.userSelect = 'none'

      this.startX = e.clientX
      this.startWidth = chatApp.offsetWidth
      this.minWidth = 400
      this.maxWidth = window.innerWidth - 400

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }

    resizeHandle.addEventListener('mousedown', onMouseDown)

    // Set the flag to true to avoid multiple event listeners
    this.isResizingInitialized = true
  }

  activateSidebarItem(event) {
    const elements = document.querySelectorAll('.sidebar-item')
    elements.forEach((element) => {
      element.classList.remove('active')
    })
    event.target.closest('.sidebar-item').classList.add('active')
  }

  toggleChatInfo() {
    const context = document.querySelector('.chat-info')
    context.classList.toggle('collapsed')
  }
}
